// Typed API client. The browser talks ONLY to the CrossCastr API (Clean Architecture — never
// the DB) via the Vite `/api` proxy. All DTO types are `import type` from @crosscastr/shared so
// zero server runtime (Prisma/BullMQ) is ever pulled into the web bundle. In dev the API's
// auth stub resolves the dev tenant with no token — so no secret ever lives in web code.

import type {
  AccountSummary,
  ActivityEntry,
  CampaignDetail,
  CampaignSummary,
  CreateCampaignRequest,
  CreateCampaignResponse,
  TranslateResponse,
  Lang,
  DashboardResponse,
  TargetSummary,
  VariationsResponse,
  Surface,
  TenantSettingsResponse,
  UpdateSettingsRequest,
  ConnectTelegramResponse,
  ChannelsPullResponse,
  SelectChannelRequest,
  ArticleResponse,
  SummarizeResponse,
  NewsSourceSummary,
  CreateNewsSourceRequest,
  UpdateNewsSourceRequest,
  NewsItemSummary,
  NewsItemStatus,
  LinksAnalyticsResponse,
  StatsGrowthResponse,
  StatsBroadcastsResponse,
  BestTimeResponse,
  LoginChallengeResponse,
  SessionResponse,
  MeResponse,
  TeamUser,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
} from '@crosscastr/shared';
import { getAccessToken, getRefreshToken, setTokens, clearSession } from './auth.js';

const BASE = '/api';

/** Attach the Bearer access token (when present) to a request. */
function withAuth(headers: HeadersInit): HeadersInit {
  const token = getAccessToken();
  return token ? { ...headers, authorization: `Bearer ${token}` } : headers;
}

// Single-flight refresh: many 401s at once share ONE /auth/refresh round-trip.
let refreshing: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const s = (await r.json()) as SessionResponse;
        setTokens(s.accessToken, s.refreshToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function raw(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: withAuth({ 'content-type': 'application/json', ...(init?.headers ?? {}) }),
  });
}

async function req<T>(path: string, init?: RequestInit, _retried = false): Promise<T> {
  const res = await raw(path, init);
  if (res.status === 401 && !_retried && !path.startsWith('/auth/')) {
    // Access token likely expired — rotate once and retry; otherwise force re-login.
    if (await tryRefresh()) return req<T>(path, init, true);
    clearSession();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error(`API 401 ${path}`);
  }
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`API ${res.status} ${path} ${detail}`);
  }
  return res.json() as Promise<T>;
}

export type RangeDays = 30 | 90 | 365;

export const api = {
  dashboard: (range: RangeDays) => req<DashboardResponse>(`/dashboard?range=${range}`),
  accounts: () => req<{ accounts: AccountSummary[] }>('/accounts').then((r) => r.accounts),
  accountQr: (id: string) =>
    req<{
      id: string;
      status: string;
      qr: string | null;
      qrExpiresAt: string | null;
      pairingCode: string | null;
      pairingExpiresAt: string | null;
    }>(`/accounts/${id}/qr`),
  createAccount: (label: string, phone?: string) =>
    req<{ id: string; label: string; status: string; usePairing?: boolean }>('/accounts', {
      method: 'POST',
      body: JSON.stringify(phone ? { label, phone } : { label }),
    }),

  // ─── group pull + sync/target picking (the persistent connect setup) ─────────────────────────
  accountGroups: (id: string) =>
    req<{
      groups: { jid: string; name: string; memberCount: number }[];
      requiresSession: boolean;
      selectedJids: string[];
      syncGroupJid: string | null;
      adminJids: string[];
    }>(`/accounts/${id}/groups`),
  setSyncGroup: (id: string, body: { groupJid: string; adminPhone?: string }) =>
    req<{ id: string; groupJid: string; adminJids: string[] }>(`/accounts/${id}/sync-group`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addTargetFromGroup: (body: {
    waAccountId: string;
    groupJid: string;
    name: string;
    memberCount?: number;
    suffix?: string;
  }) =>
    req<{ id: string; surface: Surface; name: string; suffix: string | null }>('/targets/from-group', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeTargetFromGroup: (body: { groupJid: string }) =>
    req<{ removed: number }>('/targets/from-group', { method: 'DELETE', body: JSON.stringify(body) }),
  targets: (opts?: { search?: string; surface?: Surface }) => {
    const p = new URLSearchParams();
    if (opts?.search) p.set('search', opts.search);
    if (opts?.surface) p.set('surface', opts.surface);
    const qs = p.toString();
    return req<{ targets: TargetSummary[] }>(`/targets${qs ? `?${qs}` : ''}`).then((r) => r.targets);
  },
  campaigns: () => req<{ campaigns: CampaignSummary[] }>('/campaigns').then((r) => r.campaigns),
  campaign: (id: string) => req<CampaignDetail>(`/campaigns/${id}`),
  createCampaign: (body: CreateCampaignRequest) =>
    req<CreateCampaignResponse>('/campaigns', { method: 'POST', body: JSON.stringify(body) }),
  testSend: (id: string, phone?: string) =>
    req<{ ok: boolean; phoneHint?: string }>(`/campaigns/${id}/test-send`, {
      method: 'POST',
      body: JSON.stringify(phone ? { phone } : {}),
    }),
  retryFailed: (id: string) => req<{ retried: number }>(`/campaigns/${id}/retry`, { method: 'POST' }),
  activity: (limit = 50) => req<{ activity: ActivityEntry[] }>(`/activity?limit=${limit}`).then((r) => r.activity),
  aiVariations: (text: string, count = 10) =>
    req<VariationsResponse>('/ai/variations', { method: 'POST', body: JSON.stringify({ text, count }) }),

  // ─── auto-translate HE/AR/RU (WOW #2) — template only, no PII ────────────────────────────
  translate: (text: string, targetLangs: Lang[], fromLang: Lang = 'he') =>
    req<TranslateResponse>('/translate', { method: 'POST', body: JSON.stringify({ text, targetLangs, fromLang }) }),

  // ─── settings (Telegram / inbound-forward / alerts) ─────────────────────────────────────
  settings: () => req<TenantSettingsResponse>('/settings'),
  updateSettings: (body: UpdateSettingsRequest) =>
    req<TenantSettingsResponse>('/settings', { method: 'PUT', body: JSON.stringify(body) }),
  connectTelegram: (token: string) =>
    req<ConnectTelegramResponse>('/settings/telegram', { method: 'POST', body: JSON.stringify({ token }) }),
  disconnectTelegram: () => req<ConnectTelegramResponse>('/settings/telegram', { method: 'DELETE' }),

  // ─── channels (משוך ערוצים) ─────────────────────────────────────────────────────────────
  channels: () => req<ChannelsPullResponse>('/channels'),
  selectChannel: (body: SelectChannelRequest) =>
    req<{ id: string; surface: Surface; name: string }>('/channels', { method: 'POST', body: JSON.stringify(body) }),

  // ─── article (כתבה!) ────────────────────────────────────────────────────────────────────
  article: (url: string) => req<ArticleResponse>('/article', { method: 'POST', body: JSON.stringify({ url }) }),

  // ─── summarize-to-broadcast (WOW wave #2) — public article text only, no PII ───────────────
  summarize: (url: string, targetLangs?: Lang[]) =>
    req<SummarizeResponse>('/summarize', { method: 'POST', body: JSON.stringify({ url, targetLangs }) }),

  // ─── news autopilot (WOW wave #2) ──────────────────────────────────────────────────────
  newsSources: () => req<{ sources: NewsSourceSummary[] }>('/news/sources').then((r) => r.sources),
  createNewsSource: (body: CreateNewsSourceRequest) =>
    req<{ id: string }>('/news/sources', { method: 'POST', body: JSON.stringify(body) }),
  updateNewsSource: (id: string, body: UpdateNewsSourceRequest) =>
    req<{ id: string }>(`/news/sources/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteNewsSource: (id: string) => req<{ ok: boolean }>(`/news/sources/${id}`, { method: 'DELETE' }),
  newsItems: (status: NewsItemStatus = 'pending') =>
    req<{ items: NewsItemSummary[] }>(`/news/items?status=${status}`).then((r) => r.items),
  approveNewsItem: (id: string) =>
    req<{ campaignId: string; status: string }>(`/news/items/${id}/approve`, { method: 'POST' }),
  skipNewsItem: (id: string) => req<{ ok: boolean }>(`/news/items/${id}/skip`, { method: 'POST' }),

  // ─── link click analytics (WOW #1) ─────────────────────────────────────────────────────
  links: (campaignId?: string) =>
    req<LinksAnalyticsResponse>(`/analytics/links${campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : ''}`),

  // ─── best-time-to-send heatmap (WOW wave #2) ────────────────────────────────────────────
  bestTime: () => req<BestTimeResponse>('/analytics/best-time'),
  // ─── statistics: audience growth + per-broadcast views (mirrors bizz-pilot) ──────────────
  statsGrowth: (days = 90) => req<StatsGrowthResponse>(`/stats/growth?days=${days}`),
  statsBroadcasts: () => req<StatsBroadcastsResponse>('/stats/broadcasts'),


  // ─── auth (login → OTP → session) ────────────────────────────────────────────────────────
  login: (email: string, password: string) =>
    req<LoginChallengeResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  verifyOtp: (challengeId: string, code: string) =>
    req<SessionResponse>('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ challengeId, code }) }),
  resendOtp: (challengeId: string) =>
    req<{ ok: boolean; phoneHint: string }>('/auth/otp/resend', { method: 'POST', body: JSON.stringify({ challengeId }) }),
  me: () => req<MeResponse>('/auth/me'),
  logout: (refreshToken: string | null) =>
    req<void>('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }).catch(() => undefined),

  // ─── team / users (owner/admin) ──────────────────────────────────────────────────────────
  users: () => req<{ users: TeamUser[] }>('/users').then((r) => r.users),
  createUser: (body: CreateUserRequest) => req<CreateUserResponse>('/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: UpdateUserRequest) =>
    req<{ user: TeamUser }>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.user),
};
